// ═══════════════════════════════════════════════════════════════════════════════
// MoonbeamCreatorModals.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// Standalone modal dialogs used throughout the MoonbeamCreator send flow.
// Each modal is a full-screen overlay with backdrop blur.
//
// ─── CONTENTS ─────────────────────────────────────────────────────────────────
//
//  MODALS:
//
//    • ScheduleSendModal
//      – Gmail-inspired schedule picker for "Schedule Send" delivery mode
//      – Quick options: Tomorrow morning, This/Tomorrow afternoon, Monday morning
//      – Custom date/time picker with built-in calendar (month navigation,
//        past-day disabling, day selection) and Gmail-style text time input
//      – Shows user's detected timezone
//      – Props: scheduleDate, onScheduleDateChange, onClose
//
//    • BirthdayTimeModal
//      – Picks what time of day birthday cards should arrive
//      – Preset times: Early morning (7 AM), Morning (9 AM), Afternoon (12 PM),
//        Evening (6 PM)
//      – Gmail-style text time input for custom time
//      – Note: "Cards are sent at this time in each recipient's local timezone"
//      – Props: birthdayTime, onBirthdayTimeChange, onClose
//
//    • BirthdaySwitchModal
//      – Confirmation dialog shown when user switches to "Birthday" delivery
//        mode and some recipients don't have birthdays on file
//      – Shows count of recipients that will be removed
//      – "Cancel" or "Remove & Switch" actions
//      – Props: count, onConfirm, onCancel
//
//    • MusicSelectorModal
//      – Full music chooser for the eCard background music
//      – "No Music" option + list of available tracks
//      – Radio-button style selection
//      – Per-track audio preview button (play/pause). Only one track plays at
//        a time; selecting a different preview pauses the current one. Stops
//        on modal close. Hidden for tracks without an audio URL (engraved
//        music with musictype='code').
//      – Tells the parent (via onPreviewStart / onPreviewStop) when a preview
//        starts and stops, so the parent can mute the card iframe's audio
//        while the modal preview is playing — preventing two tracks from
//        overlapping.
//      – Expandable license/attribution details per track (license name, type,
//        URL, description, attribution text)
//      – Props: value, onChange, options, onClose, onPreviewStart, onPreviewStop
//
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { TimeInput } from './MoonbeamCreatorShared';

// ─── Schedule Send Modal (Gmail-inspired) ─────────────────────────────────────

export function ScheduleSendModal({ scheduleDate, onScheduleDateChange, onClose }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('08:00');
  const [error, setError] = useState('');

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Quick options
  const tomorrowMorning = new Date(today);
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
  tomorrowMorning.setHours(8, 0, 0, 0);

  const isBefore1PM = now.getHours() < 13;
  const afternoonDate = new Date(today);
  if (isBefore1PM) {
    afternoonDate.setHours(13, 0, 0, 0);
  } else {
    afternoonDate.setDate(afternoonDate.getDate() + 1);
    afternoonDate.setHours(13, 0, 0, 0);
  }

  const nextMonday = new Date(today);
  const dayOfWeek = nextMonday.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 7 : 8 - dayOfWeek;
  nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
  nextMonday.setHours(8, 0, 0, 0);

  const formatQuickDate = (d) => {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const toLocalDatetimeString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const selectQuickOption = (d) => {
    onScheduleDateChange(toLocalDatetimeString(d));
    onClose();
  };

  const handleCustomConfirm = () => {
    setError('');
    
    if (!customDate || !customTime) {
      setError('Please select a date and time');
      return;
    }
    
    // Validate the selected date/time is in the future
    const selectedDateTime = new Date(`${customDate}T${customTime}`);
    const nowWithBuffer = new Date();
    // Add 1 minute buffer to avoid edge cases
    nowWithBuffer.setMinutes(nowWithBuffer.getMinutes() + 1);
    
    if (selectedDateTime <= nowWithBuffer) {
      setError('Please select a time in the future');
      return;
    }
    
    onScheduleDateChange(`${customDate}T${customTime}`);
    onClose();
  };

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'your timezone';

  // Calendar state
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calYear, setCalYear] = useState(now.getFullYear());

  const calDaysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const calFirstDay = new Date(calYear, calMonth, 1).getDay();
  const calMonthName = new Date(calYear, calMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const selectCalDay = (day) => {
    const m = String(calMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    setCustomDate(`${calYear}-${m}-${d}`);
    setError(''); // Clear error when user selects a new date
  };

  const isPastDay = (day) => {
    const d = new Date(calYear, calMonth, day);
    return d < today;
  };

  const selectedCalDay = customDate ? parseInt(customDate.split('-')[2]) : null;
  const selectedCalMonth = customDate ? parseInt(customDate.split('-')[1]) - 1 : null;
  const selectedCalYear = customDate ? parseInt(customDate.split('-')[0]) : null;

  // Check if selected date is today (for showing time warning)
  const isToday = customDate && (() => {
    const selected = new Date(customDate);
    return selected.getFullYear() === now.getFullYear() &&
           selected.getMonth() === now.getMonth() &&
           selected.getDate() === now.getDate();
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">Schedule Send</h2>
            <p className="text-white/70 text-sm">{timezone}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!showCustom ? (
            <div className="space-y-2">
              {/* Quick option: Tomorrow morning */}
              <button
                onClick={() => selectQuickOption(tomorrowMorning)}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60 transition-all"
              >
                <span className="text-white text-base">Tomorrow morning</span>
                <span className="text-white/70 text-sm">{formatQuickDate(tomorrowMorning)}</span>
              </button>

              {/* Quick option: This/Tomorrow afternoon */}
              <button
                onClick={() => selectQuickOption(afternoonDate)}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60 transition-all"
              >
                <span className="text-white text-base">{isBefore1PM ? 'This afternoon' : 'Tomorrow afternoon'}</span>
                <span className="text-white/70 text-sm">{formatQuickDate(afternoonDate)}</span>
              </button>

              {/* Quick option: Monday morning */}
              <button
                onClick={() => selectQuickOption(nextMonday)}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60 transition-all"
              >
                <span className="text-white text-base">Monday morning</span>
                <span className="text-white/70 text-sm">{formatQuickDate(nextMonday)}</span>
              </button>

              {/* Custom option */}
              <button
                onClick={() => setShowCustom(true)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg border border-royal-600 bg-royal-900/40 hover:border-gold/50 transition-all mt-3"
              >
                <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gold text-base font-medium">Pick date & time</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Error message */}
              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Calendar */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}
                    className="text-white hover:text-gold transition-colors p-1">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span className="text-white font-medium text-base">{calMonthName}</span>
                  <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}
                    className="text-white hover:text-gold transition-colors p-1">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div key={d} className="text-white/50 text-xs py-1">{d}</div>
                  ))}
                  {Array.from({ length: calFirstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                  {Array.from({ length: calDaysInMonth }, (_, i) => i + 1).map((day) => {
                    const past = isPastDay(day);
                    const isSelected = selectedCalDay === day && selectedCalMonth === calMonth && selectedCalYear === calYear;
                    return (
                      <button
                        key={day}
                        onClick={() => !past && selectCalDay(day)}
                        disabled={past}
                        className={`py-1.5 rounded-lg text-sm transition-colors ${
                          past ? 'text-white/20 cursor-not-allowed'
                            : isSelected ? 'bg-gold text-royal-900 font-bold'
                            : 'text-white hover:bg-royal-800 hover:text-gold'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date + Time display */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-sm mb-1">Date</label>
                  <div className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base">
                    {customDate
                      ? new Date(customDate + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Select a date'}
                  </div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-1">Time</label>
                  <TimeInput
                    value={customTime}
                    onChange={(time) => { setCustomTime(time); setError(''); }}
                    inputClassName="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold/50 transition-colors"
                  />
                </div>
              </div>

              {/* Today warning */}
              {isToday && (
                <p className="text-amber-400/80 text-sm px-1">
                  ⚠️ Scheduling for today — make sure the time is still in the future.
                </p>
              )}

              {/* Buttons */}
              <div className="flex justify-between pt-2">
                <button onClick={() => { setShowCustom(false); setError(''); }} className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleCustomConfirm}
                  disabled={!customDate}
                  className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
                >
                  Schedule send
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Birthday Time Modal ──────────────────────────────────────────────────────

export function BirthdayTimeModal({ birthdayTime, onBirthdayTimeChange, onClose }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customTime, setCustomTime] = useState(birthdayTime || '09:00');

  const presets = [
    { label: 'Early morning', time: '07:00', display: '7:00 AM' },
    { label: 'Morning', time: '09:00', display: '9:00 AM' },
    { label: 'Afternoon', time: '12:00', display: '12:00 PM' },
    { label: 'Evening', time: '18:00', display: '6:00 PM' },
  ];

  const selectPreset = (time) => {
    onBirthdayTimeChange(time);
    onClose();
  };

  const handleCustomConfirm = () => {
    onBirthdayTimeChange(customTime);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm max-h-[85vh] flex flex-col sm:mx-4">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">Birthday Delivery Time</h2>
            <p className="text-white/70 text-sm">What time should birthday cards arrive?</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-2">
          {presets.map((p) => {
            const isDefault = p.time === '09:00';
            return (
              <button
                key={p.time}
                onClick={() => selectPreset(p.time)}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-lg border transition-all ${
                  isDefault ? 'border-gold/50 bg-gold/10' : 'border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60'
                }`}
              >
                <span className={`text-base ${isDefault ? 'text-gold font-medium' : 'text-white'}`}>{p.label}</span>
                <span className="text-white/70 text-sm">{p.display}</span>
              </button>
            );
          })}

          {!showCustom ? (
            <button
              onClick={() => setShowCustom(true)}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg border border-royal-600 bg-royal-900/40 hover:border-gold/50 transition-all mt-2"
            >
              <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gold text-base font-medium">Custom time</span>
            </button>
          ) : (
            <div className="flex items-center gap-3 mt-2 px-4 py-3 border border-royal-600 rounded-lg bg-royal-900/40">
              <TimeInput
                value={customTime}
                onChange={(time) => setCustomTime(time)}
                className="flex-1"
                inputClassName="w-full px-3 py-2 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold/50 transition-colors"
              />
              <button onClick={handleCustomConfirm} className="px-4 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors flex-shrink-0">
                Set time
              </button>
            </div>
          )}

          <p className="text-white/50 text-sm mt-3 px-1">
            Cards are sent at this time in each recipient's local timezone.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Birthday Switch Confirmation Modal ───────────────────────────────────────

export function BirthdaySwitchModal({ count, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-sm p-6 space-y-4">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gold/10 flex items-center justify-center">
            <span className="text-2xl">🎂</span>
          </div>
          <h3 className="font-display text-lg text-gold mb-2">Switch to Birthday Delivery?</h3>
          <p className="text-white text-base leading-relaxed">
            <strong className="text-white">{count}</strong> recipient{count !== 1 ? 's' : ''} without
            a birthday on file will be removed from your list.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 text-white border border-royal-600 rounded-lg text-base hover:text-white hover:border-royal-200 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">
            Remove & Switch
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Music Selector Modal ─────────────────────────────────────────────────────

export function MusicSelectorModal({ value, onChange, options, onClose, onPreviewStart, onPreviewStop }) {
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpand = (musicid) => setExpandedId(expandedId === musicid ? null : musicid);

  // ─── PREVIEW PLAYBACK ────────────────────────────────────────────────────
  // A single shared <audio> element handles all preview playback. Clicking
  // play on a different track pauses the current one and starts the new one,
  // so only one preview is ever audible at a time. Closing the modal stops
  // any preview that's still playing.
  //
  // We track which track is currently playing in `playingId` so we can swap
  // the play icon to a pause icon and highlight the active row. The audio
  // element is hidden — the user only interacts with the buttons.
  //
  // PARENT COORDINATION:
  // The card iframe behind this modal may have its own music playing if the
  // user has already opened the envelope. To avoid two tracks overlapping,
  // we tell the parent to mute the iframe whenever a preview starts and
  // unmute it when the preview stops or the modal closes.
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  // Stop any preview when the modal unmounts. Without this, navigating away
  // mid-preview would leave the audio element playing in memory until GC.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Wire up audio element listeners so we update `playingId` when the track
  // ends naturally or when the browser pauses it for any reason.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleEnd = () => {
      setPlayingId(null);
      // Track ended on its own — restore card audio.
      onPreviewStop?.();
    };
    const handlePause = () => {
      // Only clear if the audio is genuinely paused (not just buffering).
      // `paused` is true after the .pause() call OR after the track ends.
      if (audio.paused) {
        setPlayingId(null);
        // Don't call onPreviewStop here — togglePreview/handleClose are
        // responsible. Calling it on every pause would unmute the card
        // briefly during track switches, causing audio flicker.
      }
    };
    audio.addEventListener('ended', handleEnd);
    audio.addEventListener('pause', handlePause);
    return () => {
      audio.removeEventListener('ended', handleEnd);
      audio.removeEventListener('pause', handlePause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePreview = (track) => {
    const audio = audioRef.current;
    if (!audio) return;

    // If this track is already playing, pause it AND restore card audio.
    if (playingId === track.musicid) {
      audio.pause();
      setPlayingId(null);
      onPreviewStop?.();
      return;
    }

    // Otherwise, swap to this track and play.
    // Setting src on an already-playing element implicitly stops the current
    // track, but we explicitly pause first to avoid play() racing with the
    // src swap (some browsers throw an AbortError otherwise).
    audio.pause();
    audio.src = track.musicaudiourl;
    audio.currentTime = 0;

    // Tell the parent to mute the card iframe BEFORE play() resolves so we
    // don't get a brief moment of overlap. Mute is idempotent — calling it
    // when nothing's playing in the iframe is a no-op.
    onPreviewStart?.();

    audio.play().then(() => {
      setPlayingId(track.musicid);
    }).catch((err) => {
      // Autoplay can be blocked on rare devices/browsers. Fail silently —
      // the user can tap again. We don't surface an error UI for this since
      // it's a soft failure on a non-critical preview.
      console.warn('Music preview playback failed:', err);
      setPlayingId(null);
      // Playback failed — undo the mute so the card audio doesn't get stuck
      // silenced for no reason.
      onPreviewStop?.();
    });
  };

  // Stop preview audio when the user picks a track or closes the modal —
  // the user has either committed to using the music (which the card iframe
  // handles via re-render) or they're done browsing. Either way, the preview
  // shouldn't keep playing under the modal.
  const stopPreview = () => {
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
  };

  const handleSelect = (musicid) => {
    onChange(musicid);
    // Don't auto-close; the user might want to compare multiple tracks before
    // committing. They click "Done" when finished.
  };

  const handleClose = () => {
    stopPreview();
    // Restore card audio whenever the modal closes, regardless of whether a
    // preview was actively playing at the moment.
    onPreviewStop?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col sm:mx-4">
        {/* Hidden shared audio element used by all preview buttons */}
        <audio ref={audioRef} preload="none" />

        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">Choose Music</h2>
            <p className="text-white/70 text-sm">Tap ▶ to preview, then select</p>
          </div>
          <button onClick={handleClose} className="text-white/70 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2">
          <button onClick={() => handleSelect(null)}
            className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center gap-3 ${
              !value ? 'border-gold bg-gold/10' : 'border-royal-700 bg-royal-900/30 hover:border-royal-500'
            }`}>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${!value ? 'border-gold' : 'border-royal-600'}`}>
              {!value && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
            </div>
            <div>
              <p className={`text-base font-medium ${!value ? 'text-gold' : 'text-white'}`}>🔇 No Music</p>
              <p className="text-white/70 text-sm">Card opens silently</p>
            </div>
          </button>
          {options.map((m) => {
            const isSelected = String(value) === String(m.musicid);
            const isExpanded = expandedId === m.musicid;
            const isPlaying = playingId === m.musicid;
            const hasAttribution = m.attributionname || m.licensetype || m.licenseurl || m.licensedescription;
            // Only audio tracks (with a URL) can be previewed. Engraved
            // music (musictype === 'code') has no audio file to play.
            const canPreview = !!m.musicaudiourl;
            return (
              <div key={m.musicid} className={`rounded-xl border transition-all overflow-hidden ${isSelected ? 'border-gold bg-gold/10' : 'border-royal-700 bg-royal-900/30 hover:border-royal-500'}`}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <button onClick={() => handleSelect(String(m.musicid))} className="flex-shrink-0" aria-label={`Select ${m.name}`}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-gold' : 'border-royal-600'}`}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
                    </div>
                  </button>

                  {/* Per-track preview button — hidden for tracks without audio */}
                  {canPreview && (
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePreview(m); }}
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                        isPlaying
                          ? 'bg-gold text-royal-900 hover:bg-gold-light'
                          : 'bg-royal-800 border border-royal-600 text-white hover:text-gold hover:border-gold/50'
                      }`}
                      aria-label={isPlaying ? `Pause ${m.name} preview` : `Preview ${m.name}`}
                      title={isPlaying ? 'Pause preview' : 'Preview'}
                    >
                      {isPlaying ? (
                        // Pause icon
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="5" width="4" height="14" rx="1" />
                          <rect x="14" y="5" width="4" height="14" rx="1" />
                        </svg>
                      ) : (
                        // Play icon
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </button>
                  )}

                  <button onClick={() => handleSelect(String(m.musicid))} className="flex-1 min-w-0 text-left">
                    <p className={`text-base font-medium truncate ${isSelected ? 'text-gold' : 'text-white'}`}>🎵 {m.name}</p>
                    {m.composer && <p className="text-white/70 text-sm truncate">by {m.composer}</p>}
                  </button>
                  {hasAttribution && (
                    <button onClick={(e) => { e.stopPropagation(); toggleExpand(m.musicid); }}
                      className="text-white/70 hover:text-gold transition-colors flex-shrink-0 p-1" title="License details">
                      <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                {isExpanded && hasAttribution && (
                  <div className="px-4 pb-3 pt-1 border-t border-royal-700/30 ml-8 mr-4">
                    <p className="text-white text-sm font-medium mb-2">License Information</p>
                    {m.attributionname && <div className="flex items-start gap-2 mb-1.5"><span className="text-white/70 text-sm flex-shrink-0">License:</span><span className="text-white text-sm">{m.attributionname}</span></div>}
                    {m.licensetype && <div className="flex items-start gap-2 mb-1.5"><span className="text-white/70 text-sm flex-shrink-0">Type:</span><span className="text-sm px-2 py-0.5 rounded bg-royal-800 text-white">{m.licensetype}</span></div>}
                    {m.licenseurl && <div className="flex items-start gap-2 mb-1.5"><span className="text-white/70 text-sm flex-shrink-0">URL:</span><a href={m.licenseurl} target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-light text-sm underline break-all">{m.licenseurl}</a></div>}
                    {m.licensedescription && <div className="mt-2"><p className="text-white text-sm">{m.licensedescription}</p></div>}
                    {m.attribution && <div className="mt-2 pt-2 border-t border-royal-700/30"><p className="text-white/80 text-sm italic">{m.attribution}</p></div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-royal-700/50 flex-shrink-0">
          <button onClick={handleClose} className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">Done</button>
        </div>
      </div>
    </div>
  );
}